import { useState } from 'react'
import {
  Plane,
  Calendar,
  Clock,
  Wrench,
  AlertTriangle,
  CheckCircle2,
  FileText,
  ChevronDown,
  TrendingUp,
  Package,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

interface Aircraft {
  tailNumber: string
  type: string
  msn: string
  registration: string
  flightHours: number
  cycles: number
  lastMaintenance: string
  nextMaintenance: string
  status: 'active' | 'maintenance' | 'aog'
}

interface MaintenanceEvent {
  id: string
  date: string
  type: string
  description: string
  ataChapter: string
  workOrder: string
  duration: string
  technician: string
  status: 'completed' | 'deferred' | 'in-progress'
}

interface DefectRecord {
  id: string
  date: string
  defect: string
  ataChapter: string
  resolution: string
  deferral?: string
  melCategory?: string
}

interface ComponentReplacement {
  id: string
  date: string
  component: string
  partNumberOff: string
  partNumberOn: string
  serialNumberOff: string
  serialNumberOn: string
  reason: string
  tsn: number
  csn: number
}

// Sample aircraft data
const aircraftList: Aircraft[] = [
  {
    tailNumber: 'N12345',
    type: 'B737-800',
    msn: '34521',
    registration: 'N12345',
    flightHours: 45230,
    cycles: 28450,
    lastMaintenance: '2026-01-15',
    nextMaintenance: '2026-02-15',
    status: 'active',
  },
  {
    tailNumber: 'N67890',
    type: 'A320-200',
    msn: '5892',
    registration: 'N67890',
    flightHours: 52100,
    cycles: 31200,
    lastMaintenance: '2026-01-20',
    nextMaintenance: '2026-02-08',
    status: 'active',
  },
  {
    tailNumber: 'N11111',
    type: 'B737-MAX',
    msn: '62015',
    registration: 'N11111',
    flightHours: 12500,
    cycles: 8900,
    lastMaintenance: '2026-01-28',
    nextMaintenance: '2026-02-05',
    status: 'maintenance',
  },
]

// Sample maintenance history
const maintenanceHistory: MaintenanceEvent[] = [
  {
    id: '1',
    date: '2026-01-28',
    type: 'C-Check',
    description: 'Comprehensive structural inspection and systems check',
    ataChapter: 'Multiple',
    workOrder: 'WO-2026-0128',
    duration: '14 days',
    technician: 'Team Alpha',
    status: 'in-progress',
  },
  {
    id: '2',
    date: '2026-01-15',
    type: 'A-Check',
    description: 'Routine inspection and servicing',
    ataChapter: 'Multiple',
    workOrder: 'WO-2026-0115',
    duration: '2 days',
    technician: 'Team Beta',
    status: 'completed',
  },
  {
    id: '3',
    date: '2025-12-20',
    type: 'Line Maintenance',
    description: 'Engine borescope inspection',
    ataChapter: 'ATA 72',
    workOrder: 'WO-2025-1220',
    duration: '8 hours',
    technician: 'Rodriguez, M.',
    status: 'completed',
  },
  {
    id: '4',
    date: '2025-12-05',
    type: 'Unscheduled',
    description: 'Hydraulic pump replacement - System A',
    ataChapter: 'ATA 29',
    workOrder: 'WO-2025-1205',
    duration: '6 hours',
    technician: 'Chen, S.',
    status: 'completed',
  },
  {
    id: '5',
    date: '2025-11-18',
    type: 'AD Compliance',
    description: 'FAA AD 2025-22-15 - Fuel tank inspection',
    ataChapter: 'ATA 28',
    workOrder: 'WO-2025-1118',
    duration: '4 hours',
    technician: 'Williams, J.',
    status: 'completed',
  },
]

// Sample defect records
const defectRecords: DefectRecord[] = [
  {
    id: '1',
    date: '2026-01-25',
    defect: 'APU bleed valve slow to respond',
    ataChapter: 'ATA 49-11',
    resolution: 'Adjusted valve timing per AMM',
    deferral: 'MEL 49-11-01',
    melCategory: 'C',
  },
  {
    id: '2',
    date: '2026-01-18',
    defect: 'Cabin temperature fluctuation Zone 2',
    ataChapter: 'ATA 21-51',
    resolution: 'Pack valve controller replaced',
  },
  {
    id: '3',
    date: '2026-01-10',
    defect: 'Landing gear position indicator intermittent',
    ataChapter: 'ATA 32-31',
    resolution: 'Proximity sensor replaced',
  },
  {
    id: '4',
    date: '2025-12-28',
    defect: 'Engine oil quantity indication erratic',
    ataChapter: 'ATA 79-31',
    resolution: 'Oil quantity transmitter replaced',
  },
]

// Sample component replacements
const componentReplacements: ComponentReplacement[] = [
  {
    id: '1',
    date: '2026-01-18',
    component: 'Pack Valve Controller',
    partNumberOff: '2950100-2',
    partNumberOn: '2950100-3',
    serialNumberOff: 'PV-45892',
    serialNumberOn: 'PV-67234',
    reason: 'Erratic operation',
    tsn: 12450,
    csn: 8920,
  },
  {
    id: '2',
    date: '2025-12-28',
    component: 'Oil Quantity Transmitter',
    partNumberOff: '5265-1',
    partNumberOn: '5265-1',
    serialNumberOff: 'OQT-8923',
    serialNumberOn: 'OQT-9145',
    reason: 'Erratic indication',
    tsn: 8920,
    csn: 6100,
  },
  {
    id: '3',
    date: '2025-12-05',
    component: 'Hydraulic Pump - System A',
    partNumberOff: '65-73456-201',
    partNumberOn: '65-73456-201',
    serialNumberOff: 'HP-12345',
    serialNumberOn: 'HP-67890',
    reason: 'Low pressure output',
    tsn: 15200,
    csn: 10500,
  },
]

// Flight hours trend data
const flightHoursTrend = [
  { month: 'Aug', hours: 380 },
  { month: 'Sep', hours: 420 },
  { month: 'Oct', hours: 395 },
  { month: 'Nov', hours: 410 },
  { month: 'Dec', hours: 350 },
  { month: 'Jan', hours: 285 },
]

export function AircraftHistoryPage() {
  const [selectedAircraft, setSelectedAircraft] = useState<Aircraft>(aircraftList[0])
  const [activeTab, setActiveTab] = useState<'maintenance' | 'defects' | 'components'>('maintenance')

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800'
      case 'aog':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getEventStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'in-progress':
        return 'bg-blue-100 text-blue-800'
      case 'deferred':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Aircraft History</h1>
          <p className="text-gray-600">Comprehensive maintenance records and component tracking</p>
        </div>
        <Button>
          <FileText className="mr-2 h-4 w-4" />
          Export History
        </Button>
      </div>

      {/* Aircraft Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <label className="font-medium text-gray-700">Select Aircraft:</label>
            <div className="relative">
              <select
                value={selectedAircraft.tailNumber}
                onChange={(e) => {
                  const aircraft = aircraftList.find((a) => a.tailNumber === e.target.value)
                  if (aircraft) setSelectedAircraft(aircraft)
                }}
                className="appearance-none rounded-lg border border-gray-200 bg-white px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-voltaire-blue"
              >
                {aircraftList.map((aircraft) => (
                  <option key={aircraft.tailNumber} value={aircraft.tailNumber}>
                    {aircraft.tailNumber} - {aircraft.type}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Aircraft Overview */}
      <div className="grid gap-4 lg:grid-cols-4">
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-navy-900">
                  <Plane className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{selectedAircraft.tailNumber}</h2>
                  <p className="text-gray-600">{selectedAircraft.type}</p>
                  <p className="text-sm text-gray-500">MSN: {selectedAircraft.msn}</p>
                </div>
              </div>
              <Badge className={getStatusColor(selectedAircraft.status)}>
                {selectedAircraft.status.toUpperCase()}
              </Badge>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="rounded-lg bg-gray-50 p-4">
                <div className="flex items-center gap-2 text-gray-500">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">Flight Hours</span>
                </div>
                <p className="mt-1 text-2xl font-bold">{selectedAircraft.flightHours.toLocaleString()}</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <div className="flex items-center gap-2 text-gray-500">
                  <TrendingUp className="h-4 w-4" />
                  <span className="text-sm">Cycles</span>
                </div>
                <p className="mt-1 text-2xl font-bold">{selectedAircraft.cycles.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-gray-500">
              <Calendar className="h-4 w-4" />
              <span className="text-sm">Last Maintenance</span>
            </div>
            <p className="mt-2 text-xl font-bold">{selectedAircraft.lastMaintenance}</p>
            <p className="text-sm text-gray-500">A-Check completed</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-gray-500">
              <Wrench className="h-4 w-4" />
              <span className="text-sm">Next Scheduled</span>
            </div>
            <p className="mt-2 text-xl font-bold">{selectedAircraft.nextMaintenance}</p>
            <p className="text-sm text-gray-500">B-Check due</p>
          </CardContent>
        </Card>
      </div>

      {/* Flight Hours Trend */}
      <Card>
        <CardHeader>
          <CardTitle>Flight Hours Trend</CardTitle>
          <CardDescription>Monthly flight hours over the last 6 months</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={flightHoursTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="hours"
                  stroke="#1a365d"
                  strokeWidth={2}
                  dot={{ fill: '#1a365d' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-4">
          {[
            { id: 'maintenance', label: 'Maintenance History', icon: Wrench },
            { id: 'defects', label: 'Defect Records', icon: AlertTriangle },
            { id: 'components', label: 'Component Replacements', icon: Package },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-navy-900 text-navy-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'maintenance' && (
        <Card>
          <CardHeader>
            <CardTitle>Maintenance History Timeline</CardTitle>
            <CardDescription>Complete maintenance record for {selectedAircraft.tailNumber}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {maintenanceHistory.map((event, index) => (
                <div
                  key={event.id}
                  className="relative flex gap-4 rounded-lg border p-4 hover:bg-gray-50"
                >
                  <div className="flex flex-col items-center">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full ${
                        event.status === 'completed'
                          ? 'bg-green-100'
                          : event.status === 'in-progress'
                          ? 'bg-blue-100'
                          : 'bg-yellow-100'
                      }`}
                    >
                      {event.status === 'completed' ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : event.status === 'in-progress' ? (
                        <Clock className="h-5 w-5 text-blue-600" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                      )}
                    </div>
                    {index < maintenanceHistory.length - 1 && (
                      <div className="mt-2 h-full w-px bg-gray-200" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{event.type}</span>
                          <Badge className={getEventStatusColor(event.status)}>{event.status}</Badge>
                        </div>
                        <p className="mt-1 text-gray-600">{event.description}</p>
                      </div>
                      <span className="text-sm text-gray-500">{event.date}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-500">
                      <span>WO: {event.workOrder}</span>
                      <span>ATA: {event.ataChapter}</span>
                      <span>Duration: {event.duration}</span>
                      <span>Tech: {event.technician}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'defects' && (
        <Card>
          <CardHeader>
            <CardTitle>Defect Records</CardTitle>
            <CardDescription>Historical defects and resolutions for {selectedAircraft.tailNumber}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-gray-500">
                    <th className="pb-3 font-medium">Date</th>
                    <th className="pb-3 font-medium">Defect</th>
                    <th className="pb-3 font-medium">ATA</th>
                    <th className="pb-3 font-medium">Resolution</th>
                    <th className="pb-3 font-medium">Deferral</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {defectRecords.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="py-3">{record.date}</td>
                      <td className="py-3 font-medium">{record.defect}</td>
                      <td className="py-3">
                        <Badge variant="outline">{record.ataChapter}</Badge>
                      </td>
                      <td className="py-3 text-gray-600">{record.resolution}</td>
                      <td className="py-3">
                        {record.deferral ? (
                          <div>
                            <span className="text-sm">{record.deferral}</span>
                            {record.melCategory && (
                              <Badge variant="secondary" className="ml-2">
                                Cat {record.melCategory}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'components' && (
        <Card>
          <CardHeader>
            <CardTitle>Component Replacement History</CardTitle>
            <CardDescription>Part changes and serialized component tracking</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {componentReplacements.map((component) => (
                <div key={component.id} className="rounded-lg border p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="font-semibold">{component.component}</span>
                      <p className="text-sm text-gray-500">{component.reason}</p>
                    </div>
                    <span className="text-sm text-gray-500">{component.date}</span>
                  </div>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div className="rounded-lg bg-red-50 p-3">
                      <p className="text-xs font-medium text-red-600">REMOVED</p>
                      <p className="mt-1 text-sm">P/N: {component.partNumberOff}</p>
                      <p className="text-sm">S/N: {component.serialNumberOff}</p>
                    </div>
                    <div className="rounded-lg bg-green-50 p-3">
                      <p className="text-xs font-medium text-green-600">INSTALLED</p>
                      <p className="mt-1 text-sm">P/N: {component.partNumberOn}</p>
                      <p className="text-sm">S/N: {component.serialNumberOn}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-4 text-sm text-gray-500">
                    <span>TSN: {component.tsn.toLocaleString()} hrs</span>
                    <span>CSN: {component.csn.toLocaleString()} cycles</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
